import styled from 'styled-components';

interface TextProps {
  variant?: 'body1' | 'body2' | 'caption' | 'overline' | 'subtitle1' | 'subtitle2';
  color?: string;
  weight?: 'normal' | 'medium' | 'semibold' | 'bold';
  align?: 'left' | 'center' | 'right' | 'justify';
  transform?: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
  decoration?: 'none' | 'underline' | 'line-through';
  truncate?: boolean;
  margin?: string;
  padding?: string;
}

const getTextStyles = (variant: string) => {
  switch (variant) {
    case 'body1':
      return {
        fontSize: '1rem',
        lineHeight: '1.5'
      };
    case 'body2':
      return {
        fontSize: '0.875rem',
        lineHeight: '1.43'
      };
    case 'caption':
      return {
        fontSize: '0.75rem',
        lineHeight: '1.33'
      };
    case 'overline':
      return {
        fontSize: '0.75rem',
        lineHeight: '2.66',
        textTransform: 'uppercase',
        letterSpacing: '0.08333em'
      };
    case 'subtitle1':
      return {
        fontSize: '1rem',
        lineHeight: '1.75',
        fontWeight: '500'
      };
    case 'subtitle2':
      return {
        fontSize: '0.875rem',
        lineHeight: '1.57',
        fontWeight: '500'
      };
    default:
      return {
        fontSize: '1rem',
        lineHeight: '1.5'
      };
  }
};

const getFontWeight = (weight: string) => {
  switch (weight) {
    case 'normal': return '400';
    case 'medium': return '500';
    case 'semibold': return '600';
    case 'bold': return '700';
    default: return '400';
  }
};

export const Text = styled.span<TextProps>`
  ${props => {
    const styles = getTextStyles(props.variant || 'body1');
    return Object.entries(styles).map(([key, value]) => 
      `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value};`
    ).join(' ');
  }}
  
  color: ${props => props.color || '#374151'};
  font-weight: ${props => getFontWeight(props.weight || 'normal')};
  text-align: ${props => props.align || 'left'};
  text-transform: ${props => props.transform || 'none'};
  text-decoration: ${props => props.decoration || 'none'};
  margin: ${props => props.margin || '0'};
  padding: ${props => props.padding || '0'};
  
  ${props => props.truncate && `
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `}
`;

// Specialized text variants
export const BodyText = styled(Text).attrs({ variant: 'body1' })``;
export const SmallText = styled(Text).attrs({ variant: 'body2' })``;
export const CaptionText = styled(Text).attrs({ variant: 'caption' })``;
export const SubtitleText = styled(Text).attrs({ variant: 'subtitle1' })``;
export const OverlineText = styled(Text).attrs({ variant: 'overline' })``;

// Semantic text variants
export const MutedText = styled(Text)`
  color: #6b7280;
`;

export const ErrorText = styled(Text)`
  color: #ef4444;
`;

export const SuccessText = styled(Text)`
  color: #10b981;
`;

export const WarningText = styled(Text)`
  color: #f59e0b;
`;

export const LinkText = styled(Text)`
  color: #667eea;
  text-decoration: underline;
  cursor: pointer;
  
  &:hover {
    color: #5a67d8;
  }
`;

export const ClickableText = styled(Text)`
  cursor: pointer;
  transition: color 0.2s ease;
  
  &:hover {
    color: #667eea;
  }
`;